/**
 * Threshold Service - Utility functions for working with average check threshold adjustments
 * @module services/thresholds/thresholdService
 *
 * This service provides functions for:
 * - Retrieving avgCheck high threshold values for stores
 * - Applying threshold adjustments to average check values for display
 * - Calculating weighted average adjustments for aggregated rows
 * - Checking if any stores have active adjustments
 *
 * IMPORTANT: All adjustments are display-time transformations only.
 * NEVER save adjusted values to the database.
 */

/**
 * Get avgCheck high threshold value for a specific store
 *
 * @param {string} storeId - Store ID to lookup (optional, for backwards compatibility)
 * @param {Array<Object>} thresholds - Threshold configuration array
 * @param {Object|string} storeThresholds - Store threshold assignments object OR threshold ID string
 * @returns {number} High threshold value in cents (0 if not configured)
 *
 * @example
 * // Using threshold ID directly (new pattern)
 * const highThreshold = getAvgCheckHighThreshold(null, thresholds, 'threshold-id-123');
 * // Returns: 1250 (representing $12.50 adjustment)
 *
 * @example
 * // Using storeThresholds object (backwards compatibility)
 * const highThreshold = getAvgCheckHighThreshold('store123', thresholds, storeThresholdsObj);
 * // Returns: 1250
 */
export function getAvgCheckHighThreshold(storeId, thresholds, storeThresholds) {
  // Guard against null/undefined thresholds
  if (!thresholds) {
    return 0;
  }

  let avgCheckThresholdId;

  // Support two patterns:
  // 1. Direct threshold ID (string)
  // 2. StoreThresholds object lookup (backwards compatibility)
  if (typeof storeThresholds === 'string') {
    avgCheckThresholdId = storeThresholds;
  } else if (storeThresholds && storeId) {
    avgCheckThresholdId = storeThresholds[storeId]?.avgCheck;
  }

  if (!avgCheckThresholdId) {
    return 0;
  }

  // Find threshold definition for avgCheck type
  const avgCheckConfig = thresholds.find(t => t.type === 'avgCheck');
  if (!avgCheckConfig) {
    return 0;
  }

  // Find specific threshold by ID
  const threshold = avgCheckConfig.thresholds.find(t => t._id === avgCheckThresholdId);
  if (!threshold) {
    return 0;
  }

  // Return highValue in cents (or 0 if not set)
  return threshold.highValue || 0;
}

/**
 * Apply threshold adjustment to average check value (for display only)
 *
 * @param {number|string} avgCheck - Original average check value (can be formatted string like "$10.50")
 * @param {number} highThresholdCents - High threshold in cents
 * @returns {number} Adjusted average check value
 *
 * @example
 * // Numeric input
 * const adjusted = applyAvgCheckAdjustment(10.00, 250);
 * // Returns: 12.50
 *
 * @example
 * // String input with formatting
 * const adjusted = applyAvgCheckAdjustment('$10.50', 250);
 * // Returns: 13.00
 */
export function applyAvgCheckAdjustment(avgCheck, highThresholdCents) {
  // Parse avgCheck to numeric value
  let numericValue = 0;

  if (typeof avgCheck === 'string') {
    // Remove all non-numeric characters except decimal point and minus sign
    numericValue = parseFloat(avgCheck.replace(/[^0-9.-]/g, ''));
  } else if (typeof avgCheck === 'number') {
    numericValue = avgCheck;
  }

  // Handle NaN from parsing
  if (isNaN(numericValue)) {
    numericValue = 0;
  }

  // Apply adjustment (convert cents to dollars)
  return numericValue + (highThresholdCents / 100);
}

/**
 * Calculate weighted average adjustment for aggregated rows
 *
 * Used for area, region, and company-level aggregations where stores may have
 * different threshold adjustments. The weighted average ensures stores with
 * higher check counts have proportionally more influence on the aggregated adjustment.
 *
 * @param {Array<Object>} stores - Array of store objects with checkCount and adjustment
 * @param {string} stores[].storeId - Store identifier
 * @param {number} stores[].checkCount - Number of checks for the store
 * @param {number} stores[].adjustment - Adjustment value in cents
 * @returns {number} Weighted average adjustment in cents
 *
 * @example
 * const stores = [
 *   { storeId: 'store1', checkCount: 1000, adjustment: 250 },
 *   { storeId: 'store2', checkCount: 500, adjustment: 150 }
 * ];
 * const weightedAdjustment = calculateWeightedAvgAdjustment(stores);
 * // Returns: ~217 cents (weighted average: (1000*250 + 500*150) / 1500 ≈ 216.67)
 *
 * Formula: Σ(storeCheckCount × storeAdjustment) / totalCheckCount
 */
export function calculateWeightedAvgAdjustment(stores) {
  // Guard against null/undefined/empty input
  if (!stores || !Array.isArray(stores) || stores.length === 0) {
    return 0;
  }

  // Calculate weighted sum and total check count
  let weightedSum = 0;
  let totalCheckCount = 0;

  stores.forEach(store => {
    // Only include stores with valid checkCount and adjustment properties
    const checkCount = store?.checkCount;
    const adjustment = store?.adjustment;

    // Skip stores missing checkCount or adjustment (but allow 0 values)
    if (checkCount === undefined || checkCount === null || checkCount <= 0) {
      return;
    }
    if (adjustment === undefined || adjustment === null) {
      return;
    }

    weightedSum += checkCount * adjustment;
    totalCheckCount += checkCount;
  });

  // Avoid division by zero
  if (totalCheckCount === 0) {
    return 0;
  }

  // Return weighted average in cents
  return weightedSum / totalCheckCount;
}

/**
 * Check if any stores have non-zero avgCheck high thresholds
 *
 * Used to determine if the AdjustmentFootnote should be displayed.
 *
 * @param {Array<string>} storeIds - Array of store IDs to check
 * @param {Array<Object>} thresholds - Threshold configuration
 * @param {Object} storeThresholds - Store threshold assignments
 * @returns {boolean} True if any store has adjustments configured
 *
 * @example
 * const hasAdjustments = hasAvgCheckAdjustments(
 *   ['store1', 'store2'],
 *   thresholds,
 *   storeThresholds
 * );
 * // Returns: true if any store has non-zero avgCheckHigh threshold
 */
export function hasAvgCheckAdjustments(storeIds, thresholds, storeThresholds) {
  // Guard against null/undefined/empty input
  if (!storeIds || !Array.isArray(storeIds) || storeIds.length === 0) {
    return false;
  }

  // Check if any store has a non-zero adjustment
  return storeIds.some(storeId => {
    const highThreshold = getAvgCheckHighThreshold(storeId, thresholds, storeThresholds);
    return highThreshold !== 0;
  });
}
